/** Reads the checked-in declarations of what each node type publishes. */

import rawDefinitions from '@/proto/variable_definitions.json';
import {
  VariableDefinitions,
  type NodeDataMatch,
  type NodeOutput,
  type NodeTypeSchema,
} from '@/proto/variable_schema';
import {
  VariableSource,
  type EnumValue,
  type VariableType,
} from '@/proto/common';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import type { NodeType } from '../nodes/registry';

// The backend validates this file at load and a backend test reads the generated copy, so
// anything malformed fails CI before it reaches here.
const DEFINITIONS = VariableDefinitions.fromJSON(rawDefinitions);

const VALUES_BY_TYPE = new Map<VariableType, EnumValue[]>(
  DEFINITIONS.enums.map((enumSchema) => [enumSchema.type, enumSchema.values])
);

// The file declares node-type outputs only, so every output it holds is marked as one here, the
// same way the backend loader marks them.
const SCHEMA_BY_NODE_TYPE = new Map<string, NodeTypeSchema>(
  DEFINITIONS.node_types.map((nodeType) => [
    nodeType.proto_oneof_key,
    {
      ...nodeType,
      outputs: nodeType.outputs.map((output) => ({
        ...output,
        source: VariableSource.SOURCE_NODE_TYPE,
      })),
    },
  ])
);

/** Every value an enum type allows, or undefined for a non-enum type. */
export function getEnumValues(type: VariableType): EnumValue[] | undefined {
  return VALUES_BY_TYPE.get(type);
}

// Every value one node setting holds, as strings. A bool reads as "true" or "false", which is how a
// condition spells it. An unset setting holds no values, so it satisfies nothing. A nested message
// that is present holds the one value "set", which is how a one-of option is tested.
//
// The backend tells a setting holding a list from one holding a single value by reading the proto
// descriptor. ts-proto emits plain interfaces with no descriptor table, so the shape is read off
// the value instead.
function getFieldValues(value: unknown): string[] {
  if (value === undefined || value === null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map((item) => String(item));
  }
  if (typeof value === 'object') {
    return ['set'];
  }
  return [String(value)];
}

// Whether a node's settings satisfy every one of these conditions.
function allMatchesHold(
  matches: NodeDataMatch[],
  settings: Record<string, unknown>
): boolean {
  for (const match of matches) {
    // A setting holding one value matches when that value is listed. A setting holding several
    // values matches when any one of them is listed.
    const heldValues = getFieldValues(settings[match.field]);
    if (!heldValues.some((value) => match.equals.includes(value))) {
      return false;
    }
  }
  return true;
}

/**
 * Every variable a node of this type can publish, whatever its settings.
 *
 * Every node type is named in the definitions file, so an unknown one throws.
 *
 * @param nodeType - The node's key in the LBNodeData oneof.
 */
export function getDeclaredOutputs(nodeType: NodeType): NodeOutput[] {
  const schema = SCHEMA_BY_NODE_TYPE.get(nodeType);
  if (!schema) {
    throw new Error(`Variable definitions name no node type '${nodeType}'.`);
  }
  return schema.outputs;
}

/**
 * Whether a test run accepts a workflow containing a node of this type.
 *
 * Every node type is named in the definitions file, so an unknown one throws.
 *
 * @param nodeType - The node's key in the LBNodeData oneof.
 */
export function supportsTestRuns(nodeType: NodeType): boolean {
  const schema = SCHEMA_BY_NODE_TYPE.get(nodeType);
  if (!schema) {
    throw new Error(`Variable definitions name no node type '${nodeType}'.`);
  }
  return schema.supports_test_runs;
}

/**
 * The variables a node of this type publishes, given how it's set up.
 *
 * Every node type is named in the definitions file, so an unknown one throws.
 *
 * @param nodeType - The node's key in the LBNodeData oneof.
 * @param nodeData - The node's data, holding its settings under that key.
 */
export function getNodeTypeOutputs(
  nodeType: NodeType,
  nodeData: LBNodeData
): NodeOutput[] {
  const settings = (nodeData[nodeType] ?? {}) as Record<string, unknown>;
  return getDeclaredOutputs(nodeType).filter((output) =>
    allMatchesHold(output.published_when, settings)
  );
}
